import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled,
} from '@mui/material'
import { Stack } from '@mui/system'
import QRCode from 'qrcode'
import { useEffect, useRef } from 'react'
import { useConfigs } from '../../context/configs_context'
import { CreditNoteInfo, ProductInSale } from '../../context/sale_context'
import { NationalityEnum } from '../../context/supplier_context'
import {
    getDateInDDMMYYYYFormat,
    getDateString,
    getIvaAmount,
    getIvaBaseExe,
    getIvaTenSubtotal,
    maskCurrency,
    numeroPorExtensoEspanhol,
} from '../../utils/maks'
import { getFileEndpoint } from '../../services/media'
import { useBranch } from '../../context/branch_context'

const StyledTableCell = styled(TableCell)(({}) => ({
    color: 'text.primary',
    border: 'none',
    padding: '1px 2px',
    fontSize: '11px',
}))

interface CreditNoteProps {
    creditNote: CreditNoteInfo
    clientVia: boolean
}

const ContactIcon: Record<string, JSX.Element> = {
    Facebook: <FacebookIcon />,
    Instagram: <InstagramIcon />,
    Whatsapp: <WhatsAppIcon />,
    Endereço: <LocationOnIcon />,
    Telefone: <LocalPhoneIcon />,
    Email: <EmailIcon />,
}

const PRODUCT_THRESHOLD = 7

function CreditNote({ creditNote, clientVia }: CreditNoteProps) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const qrCodeRef = useRef<HTMLCanvasElement | null>(null)
    const { branches } = useBranch()

    const twoPages =
        creditNote.products.length > PRODUCT_THRESHOLD ||
        creditNote.qrCode ||
        !configs.printHalfPage

    const operator = creditNote.client ?? creditNote.supplier

    const contacts = creditNote?.branch
        ? creditNote.branch.contacts
        : configs.contacts
    const companyName =
        creditNote.branch && configs.showBranchNameInNote
            ? creditNote.branch.name
            : configs.companyName
    const companyLogo = creditNote.branch?.companyLogo
        ? branches.find((b) => b.id === creditNote.branch?.id)?.companyLogo
              ?.content
        : base64Logo
    const companySlogan = creditNote.branch?.companySlogan
        ? creditNote.branch.companySlogan
        : configs.companySlogan
    const companyDocument = creditNote.branch?.companyDocument
        ? creditNote.branch.companyDocument
        : configs.companyDocument

    const discountGroups: { iva: number; prop: number; value: number }[] =
        (() => {
            if (!creditNote.discount) return []
            const saleProducts =
                creditNote.operations?.flatMap(
                    (o) => o.sale?.products ?? []
                ) ?? []
            const discountProducts: (ProductInSale |
                (typeof creditNote.products)[number])[] =
                saleProducts.length
                    ? [...saleProducts]
                    : [...creditNote.products]
            const byKey = new Map<
                string,
                { iva: number; prop: number; subtotal: number }
            >()
            for (const p of discountProducts) {
                const iva = p.ivaValue || 0
                const prop = p.proporcaoIva ?? 100
                const key = `${iva}-${prop}`
                const found = byKey.get(key)
                if (found) {
                    found.subtotal += p.subtotal || 0
                } else {
                    byKey.set(key, {
                        iva,
                        prop,
                        subtotal: p.subtotal || 0,
                    })
                }
            }
            const sorted = Array.from(byKey.values()).sort(
                (a, b) => b.iva - a.iva || b.prop - a.prop
            )
            let remaining = creditNote.discount
            const groups: { iva: number; prop: number; value: number }[] = []
            for (const g of sorted) {
                const take = Math.min(remaining, g.subtotal)
                remaining -= take
                if (take > 0)
                    groups.push({ iva: g.iva, prop: g.prop, value: take })
                if (remaining <= 0) break
            }
            if (!groups.length)
                groups.push({
                    iva: 0,
                    prop: 100,
                    value: creditNote.discount,
                })
            return groups
        })()

    const discountTotals = discountGroups.reduce(
        (acc, g) => {
            const value = g.value
            acc.exento +=
                g.iva === 0
                    ? value
                    : getIvaBaseExe(g.iva, value, g.prop)
            acc.five += g.iva === 5 ? value : 0
            acc.ten +=
                g.iva === 10
                    ? getIvaTenSubtotal(10, value, 1, g.prop)
                    : 0
            acc.ivaFive +=
                g.iva === 5 ? getIvaAmount(5, value, 1, g.prop) : 0
            acc.ivaTen +=
                g.iva === 10 ? getIvaAmount(10, value, 1, g.prop) : 0
            return acc
        },
        { exento: 0, five: 0, ten: 0, ivaFive: 0, ivaTen: 0 }
    )

    const productsSubtotals = creditNote.products.reduce(
        (acc, p) => {
            const subtotal = p.subtotal || 0
            acc.exento +=
                (p.ivaValue || 0) === 0
                    ? subtotal
                    : getIvaBaseExe(p.ivaValue, subtotal, p.proporcaoIva)
            acc.five += (p.ivaValue || 0) === 5 ? subtotal : 0
            acc.ten +=
                (p.ivaValue || 0) === 10
                    ? getIvaTenSubtotal(
                          p.ivaValue,
                          p.price,
                          p.qnt,
                          p.proporcaoIva
                      )
                    : 0
            return acc
        },
        { exento: 0, five: 0, ten: 0 }
    )

    useEffect(() => {
        if (creditNote.qrCode)
            QRCode.toCanvas(qrCodeRef.current, creditNote.qrCode, {
                width: 200,
            })
    }, [])

    return (
        <Stack
            direction="column"
            width="100%"
            px="20px"
            py="10px"
            height={twoPages ? '100vh' : '50vh'}
        >
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRight={creditNote.note ? '0' : '1px solid black'}
                    borderRadius={'12px'}
                    width={creditNote.note ? '70%' : '100%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactType.id === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactType.id ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                {creditNote.note && (
                    <Stack
                        direction={'column'}
                        border={'1px solid black'}
                        borderRadius={'12px'}
                        width={'30%'}
                    >
                        <Stack direction={'row'} p={'10px'}>
                            <Box flex={'1'}>
                                <Typography fontSize={'11px'}>
                                    TIMBRADO N {creditNote.note?.timbrado}
                                </Typography>
                                <Typography fontSize={'11px'} mt={'1px'}>
                                    RUC: {companyDocument}
                                </Typography>
                                <Typography fontSize={'11px'} mt={'1px'}>
                                    Fecha início vigência:{' '}
                                    {getDateInDDMMYYYYFormat(
                                        creditNote.note?.iniD as any
                                    )}
                                </Typography>
                                <Typography fontSize={'11px'}>
                                    Fecha fin vigência:{' '}
                                    {creditNote.note?.endD
                                        ? getDateInDDMMYYYYFormat(
                                              creditNote.note.endD
                                          )
                                        : '---'}
                                </Typography>
                                <Typography
                                    fontSize={'14px'}
                                    mt={'10px'}
                                    fontWeight={'bold'}
                                    textAlign={'center'}
                                >
                                    {creditNote.bill}
                                </Typography>
                                <Typography
                                    fontSize={'14px'}
                                    mt={'5px'}
                                    fontWeight={'bold'}
                                    textAlign={'center'}
                                >
                                    Nota de{' '}
                                    {creditNote.isDebitNote
                                        ? 'débito'
                                        : 'crédito'}
                                </Typography>
                            </Box>
                            {/* <Typography
                            sx={{
                                writingMode: 'vertical-rl',
                                textOrientation: 'mixed',
                            }}
                            p={'30px'}
                            color={'error'}
                            fontSize={'26px'}
                        >
                            {sale.bill}
                        </Typography> */}
                        </Stack>
                    </Stack>
                )}
            </Stack>
            {operator && (
                <Stack
                    width={'100%'}
                    border={'1px solid black'}
                    borderTop={'0'}
                    borderRadius={'12px'}
                    direction={'column'}
                    padding={'5px'}
                >
                    <Stack direction={'row'} alignItems={'center'}>
                        <Box width={'70%'}>
                            <Typography fontSize={'16px'} fontWeight={'bold'}>
                                Nombre: {operator?.name || '---'}
                            </Typography>
                            <Typography fontSize={'16px'} fontWeight={'bold'}>
                                {operator?.nationality ===
                                NationalityEnum.Native
                                    ? 'Ruc'
                                    : 'Documento'}
                                : {operator?.document || '---'}
                            </Typography>
                        </Box>
                        <Box>
                            <Box>
                                <Typography fontSize={'11px'}>
                                    Facturas relacionadas:{' '}
                                    {creditNote.operations
                                        ?.map((o) => {
                                            if (o.sale) return o.sale?.bill
                                            else return o.buy?.bill
                                        })
                                        .join(', ') ?? ''}
                                </Typography>
                                <Typography fontSize={'11px'}>
                                    Fecha:{' '}
                                    {creditNote.noteDate
                                        ? getDateString({
                                              date: creditNote.noteDate,
                                              hour: creditNote.noteHour,
                                              configs: configs,
                                              isSummerTime:
                                                  !!creditNote.isNoteSummerTime,
                                          })
                                        : getDateString({
                                              date: creditNote.date,
                                              hour: creditNote.hour,
                                              configs: configs,
                                              isSummerTime:
                                                  !!creditNote.isSummerTime,
                                          })}
                                </Typography>
                                <Typography fontSize={'11px'}>
                                    Telefonos:{' '}
                                    {operator?.phoneNumbers.join(', ') || '---'}
                                </Typography>
                                <Typography fontSize={'11px'}>
                                    Dirección: {operator?.address || '---'}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </Stack>
            )}
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                flex={'1'}
                justifyContent={'space-between'}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {configs.showIdOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    CÓDIGO
                                </StyledTableCell>
                            )}
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                CÓDIGO BAR.
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                CANT. / DESC.
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                P. VENTA
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                EXENTO
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                5%
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderRight: 'none',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                10%
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {creditNote.products.map((product) => (
                            <TableRow sx={{ height: '0' }}>
                                {configs.showIdOnNote && (
                                    <StyledTableCell>
                                        {product.product.internalCodes[0]}
                                    </StyledTableCell>
                                )}
                                <StyledTableCell>
                                    {product.product.barcodes[0]}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {product.qnt} / {product.product.name}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {maskCurrency(
                                        product.price,
                                        creditNote?.coin
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {maskCurrency(
                                        product.ivaValue === 0
                                            ? product.subtotal
                                            : getIvaBaseExe(
                                                  product.ivaValue,
                                                  product.subtotal,
                                                  product.proporcaoIva
                                              ),
                                        creditNote?.coin
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {maskCurrency(
                                        product.ivaValue === 5
                                            ? product.subtotal
                                            : 0,
                                        creditNote?.coin
                                    )}
                                </StyledTableCell>
                                <StyledTableCell sx={{ borderRight: 'none' }}>
                                    {maskCurrency(
                                        product.ivaValue === 10
                                            ? getIvaTenSubtotal(
                                                  product.ivaValue,
                                                  product.price,
                                                  product.qnt,
                                                  product.proporcaoIva
                                              )
                                            : 0,
                                        creditNote?.coin
                                    )}
                                </StyledTableCell>
                            </TableRow>
                        ))}
                        {creditNote.discount > 0 &&
                            discountGroups.map((g) => (
                                <TableRow
                                    key={`${g.iva}-${g.prop}`}
                                    sx={{ height: '0' }}
                                >
                                    {configs.showIdOnNote && (
                                        <StyledTableCell />
                                    )}
                                    <StyledTableCell />
                                    <StyledTableCell>
                                        1 / DESCUENTOS CONCEDIDOS
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {maskCurrency(
                                            g.value,
                                            creditNote?.coin
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {maskCurrency(
                                            g.iva === 0
                                                ? g.value
                                                : getIvaBaseExe(
                                                      g.iva,
                                                      g.value,
                                                      g.prop
                                                  ),
                                            creditNote?.coin
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {maskCurrency(
                                            g.iva === 5 ? g.value : 0,
                                            creditNote?.coin
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        sx={{ borderRight: 'none' }}
                                    >
                                        {maskCurrency(
                                            g.iva === 10
                                                ? getIvaTenSubtotal(
                                                      10,
                                                      g.value,
                                                      1,
                                                      g.prop
                                                  )
                                                : 0,
                                            creditNote?.coin
                                        )}
                                    </StyledTableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <Table>
                    <TableRow>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                                fontSize: '14px',
                            }}
                        >
                            Subtotales:
                        </StyledTableCell>
                        {configs.showIdOnNote && (
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                }}
                            ></StyledTableCell>
                        )}
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        ></StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        ></StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        >
                            {maskCurrency(
                                productsSubtotals.exento +
                                    discountTotals.exento,
                                creditNote?.coin
                            )}
                        </StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        >
                            {maskCurrency(
                                productsSubtotals.five + discountTotals.five,
                                creditNote?.coin
                            )}
                        </StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        >
                            {maskCurrency(
                                productsSubtotals.ten + discountTotals.ten,
                                creditNote?.coin
                            )}
                        </StyledTableCell>
                    </TableRow>
                </Table>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                pl={'10px'}
                pb={'5px'}
            >
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                >
                    <Typography fontSize={'12px'}>
                        Total a pagar:{' '}
                        <Box component={'span'} fontSize={'11px'}>
                            (En letras)
                        </Box>{' '}
                        {numeroPorExtensoEspanhol(creditNote?.total, creditNote.coin)}
                    </Typography>
                    <Typography
                        fontSize={'12px'}
                        borderLeft={'1px solid black'}
                        borderBottom={'1px solid black'}
                        p={'5px'}
                    >
                        {maskCurrency(creditNote.total, creditNote?.coin)}
                    </Typography>
                </Stack>
                <Stack direction={'row'} spacing={15} mt={'3px'}>
                    <Typography sx={{ fontSize: '12px' }}>
                        Liquidación del IVA:{' '}
                    </Typography>
                    <Typography sx={{ fontSize: '12px' }}>
                        (5%):{' '}
                        {maskCurrency(
                            (creditNote.totalIvaFivePercent ?? 0) +
                                discountTotals.ivaFive,
                            creditNote?.coin
                        )}
                    </Typography>
                    <Typography sx={{ fontSize: '12px' }}>
                        (10%):{' '}
                        {maskCurrency(
                            (creditNote?.totalIvaTenPercent ?? 0) +
                                discountTotals.ivaTen,
                            creditNote?.coin
                        )}
                    </Typography>
                    <Typography sx={{ fontSize: '12px' }}>
                        (T. IVA):{' '}
                        {maskCurrency(
                            (creditNote.totalIva ?? 0) +
                                discountTotals.ivaFive +
                                discountTotals.ivaTen,
                            creditNote?.coin
                        )}
                    </Typography>
                </Stack>
                <Typography mt={'3px'} fontSize={'12px'}>
                    {clientVia ? 'Original: Cliente' : 'Duplicado: Tributário'}
                </Typography>
            </Stack>
            {creditNote.qrCode && (
                <Stack
                    width={'100%'}
                    border={'1px solid black'}
                    borderTop={'0'}
                    borderRadius={'12px'}
                    p="10px"
                >
                    <Stack direction={'row'} spacing={'15px'}>
                        <Box component="canvas" ref={qrCodeRef} />
                        <Stack direction="column" py="10px" spacing={'15px'}>
                            <Typography fontSize="12px">
                                Consulte la validez de esta factura eletrónica
                                con el documento CDC impreso en:
                            </Typography>
                            <Typography fontSize="13px">
                                https://ekuatia.set.gov.py/consultas/
                            </Typography>
                            <Typography fontSize="13px">
                                CDC: {creditNote.cdc}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography fontSize="12px" mt="5px">
                        ESTE DOCUMENTO ES UNA REPRESENTACIÓN GRÁFICA DE UN
                        DOCUMENTO ELETRÓNICO (XML)
                    </Typography>
                    <Typography my="10px" fontWeight="bold" fontSize="12px">
                        Información de interés del facturador eletrónico emisor
                    </Typography>
                    <Typography fontSize="11px">
                        Si su documento eletrónico presenta algún error, podrá
                        solicitar la modifcación dentro de las 72 horas
                        siguientes de la emisión de éste comprobante
                    </Typography>
                </Stack>
            )}
            {!creditNote.qrCode && (
                <Stack
                    width={'100%'}
                    border={'1px solid black'}
                    borderTop={'0'}
                    borderRadius={'12px'}
                    p="10px"
                    pt="30px"
                    justifyContent={'center'}
                    direction={'row'}
                    spacing={'60px'}
                >
                    <Stack
                        alignItems={'center'}
                        borderTop={'1px solid black'}
                        pt={'2px'}
                        px={'30px'}
                    >
                        <Typography fontSize="11px">
                            Firma del cliente
                        </Typography>
                    </Stack>
                    <Stack
                        alignItems={'center'}
                        borderTop={'1px solid black'}
                        pt={'2px'}
                        px={'30px'}
                    >
                        <Typography fontSize="11px">
                            Aclaración de firma
                        </Typography>
                    </Stack>
                    <Typography fontSize="11px">Fecha:</Typography>
                </Stack>
            )}
        </Stack>
    )
}

interface Props {
    creditNotes: CreditNoteInfo[]
    handleClose(): void
    dontPrint?: boolean
}

export function CreditNoteDialog({
    handleClose,
    creditNotes,
    dontPrint,
}: Props) {
    const { configs } = useConfigs()
    const printRef = useRef<HTMLElement>()

    useEffect(() => {
        if (!dontPrint) {
            printRef.current!.style.visibility = 'hidden'
            printRef.current!.style.position = 'fixed'

            setTimeout(() => {
                printRef.current!.style.position = 'absolute'
                printRef.current!.className = 'printable'
                window.scroll(0, 0)
                window.print()
                handleClose()
            }, 100)
        }
    }, [])

    return (
        <Box id="note" ref={printRef}>
            <Stack direction={'column'}>
                {creditNotes.map((creditNote) => {
                    return (
                        <>
                            {Array.from({
                                length: !dontPrint ? configs.notesQnt : 1,
                            }).map((_, index) => (
                                <CreditNote
                                    creditNote={creditNote}
                                    clientVia={index % 2 === 0}
                                />
                            ))}
                        </>
                    )
                })}
            </Stack>
        </Box>
    )
}
